import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import AppImage from '../../../components/AppImage';

const ScanHistory = ({ history, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const filteredHistory = history?.filter(log => {
    const matchesSearch = log?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          log?.user_profiles?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    if (filterType === 'check_in') {
      return matchesSearch && !log?.check_out_time;
    } else if (filterType === 'check_out') {
      return matchesSearch && log?.check_out_time;
    }
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card rounded-lg p-6 border border-border">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by member name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              icon="Search"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('check_in')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'check_in' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Checked In
            </button>
            <button
              onClick={() => setFilterType('check_out')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'check_out' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Checked Out
            </button>
            <Button
              onClick={onRefresh}
              variant="outline"
              iconName="RefreshCw"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>
      {/* History List */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Check-In Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Check-Out Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Entry Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredHistory?.length > 0 ? (
                filteredHistory?.map((log) => {
                  const duration = log?.duration_minutes || 
                    (log?.check_out_time ? 
                      Math.round((new Date(log?.check_out_time) - new Date(log?.check_in_time)) / (1000 * 60)) : 
                      null);

                  return (
                    <tr key={log?.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {log?.user_profiles?.avatar_url ? (
                            <AppImage
                              src={log?.user_profiles?.avatar_url}
                              alt={log?.user_profiles?.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Icon name="User" size={20} className="text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{log?.user_profiles?.full_name}</p>
                            <p className="text-sm text-muted-foreground">{log?.user_profiles?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm">{new Date(log?.check_in_time)?.toLocaleString()}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log?.check_out_time ? (
                          <p className="text-sm">{new Date(log?.check_out_time)?.toLocaleString()}</p>
                        ) : (
                          <span className="text-sm text-muted-foreground">Still checked in</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {duration ? (
                          <p className="text-sm font-medium">{duration} min</p>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          <Icon name="Scan" size={12} />
                          {log?.entry_type?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          log?.check_out_time
                            ? 'bg-info/10 text-info' :'bg-success/10 text-success'
                        }`}>
                          <Icon name={log?.check_out_time ? 'LogOut' : 'LogIn'} size={12} />
                          {log?.check_out_time ? 'Checked Out' : 'Checked In'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Icon name="Search" size={48} className="mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground">No scan history found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ScanHistory;